import React from "react";
import { Menu, Bell, ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Notification, Tenant } from "@/types";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import BreadcrumbNav from "./breadcrumb-nav";

interface TopNavProps {
  onOpenMobileNav: () => void;
}

export default function TopNav({ onOpenMobileNav }: TopNavProps) {
  const { user, setActiveTenantContextAndRefetch } = useAuth();
  const authenticatedRequest = useAuthenticatedRequest();
  
  // Fetch tenants for SuperAdmin dropdown
  const { data: tenantsList } = useQuery<Tenant[], Error>({
    queryKey: ["/api/tenants/list-for-admin", user?.userId],
    queryFn: async () => {
      if (!user?.isSuperAdmin) return []; 
      
      const res = await authenticatedRequest('/api/tenants/list-for-admin');
      if (!res.ok) throw new Error("Failed to fetch tenants for admin: " + res.statusText);
      return res.json();
    },
    enabled: !!user?.isSuperAdmin,
  });

  // Temporarily disable notifications until backend endpoint is implemented
  const { data: notifications } = useQuery<Notification[], Error>({
    queryKey: ["/api/notifications", user?.userId],
    queryFn: async () => {
        const res = await authenticatedRequest('/api/notifications');
        if (!res.ok) throw new Error("Failed to fetch notifications: " + res.statusText);
        return res.json();
    },
    enabled: false, // Disabled until backend endpoint is implemented
  });

  const unreadCount = notifications?.filter(n => !n.isRead).length || 0;

  const getActiveContextDisplay = () => {
    if (!user?.isSuperAdmin) return null;
    // Ensure user.activeTenantContext is accessed safely, it can be undefined from useAuth initially
    const currentContext = user.activeTenantContext;
    if (currentContext === "__ALL__") return "All Tenants";
    if (currentContext === null || currentContext === undefined) return "No Tenant";
    const selectedTenant = tenantsList?.find((t: Tenant) => t.tenantId === currentContext);
    return selectedTenant?.companyName || "Select Tenant";
  };

  return (
    <div className="bg-white shadow-sm z-10">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center md:hidden">
              <button 
                type="button" 
                className="text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-md text-sm font-medium"
                onClick={onOpenMobileNav}
              >
                <Menu className="text-xl" />
              </button>
            </div>
            
            {/* SuperAdmin Tenant Context Switcher */}
            {user?.isSuperAdmin && (
              <div className="hidden sm:ml-4 sm:flex sm:items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="flex items-center text-sm">
                      {getActiveContextDisplay()}
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuLabel>Switch Tenant Context</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveTenantContextAndRefetch(null);
                      }}
                      className={cn(
                        "cursor-pointer",
                        user.activeTenantContext === null && "bg-accent"
                      )}
                    >
                      No Tenant
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setActiveTenantContextAndRefetch && setActiveTenantContextAndRefetch("__ALL__")}>
                      All Tenants
                    </DropdownMenuItem>
                    {tenantsList && tenantsList.length > 0 && <DropdownMenuSeparator />}
                    {tenantsList?.map((tenant: Tenant) => (
                      <DropdownMenuItem key={tenant.tenantId} onSelect={() => setActiveTenantContextAndRefetch && setActiveTenantContextAndRefetch(tenant.tenantId)}>
                        {tenant.companyName}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <div className="hidden sm:ml-6 sm:flex sm:items-center">
              {/* Breadcrumb navigation */}
              <BreadcrumbNav />
            </div>
          </div>
          
          <div className="flex items-center">
            
            
            <div className="flex-shrink-0 relative mr-3">
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-destructive text-xs text-white font-medium flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="p-3 border-b border-border">
                    <h3 className="font-medium">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications && notifications.length > 0 ? (
                      notifications.map((notification: Notification) => (
                        <div 
                          key={notification.notificationId} 
                          className={`p-3 border-b border-border ${!notification.isRead ? 'bg-muted/30' : ''}`}
                        >
                          <p className="text-sm">{notification.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(notification.createdAt).toLocaleString()}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-center text-muted-foreground">
                        No notifications
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="hidden md:block">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary flex items-center justify-center text-white font-medium">
                  {user?.fullName?.split(' ').map(n => n[0]).join('') || user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="ml-2">
                  <div className="text-sm font-medium text-neutral-700">
                    {user?.fullName || user?.email}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
